// @flow
import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Button from '@localytics/loc-components/lib/Button';
import Tooltip from '@localytics/loc-components/lib/Tooltip';
import DurationPicker from './DurationPicker';
import DeleteNodeModal from './DeleteNodeModal';
import { StandardNode } from './StandardNode';
import { startDeletingNode, stopDeletingNode } from '../actions/tree';
import { DELAY_TYPE, ADD_NODE_TYPE } from '../constants/ApiTypes';
import { DELAY_ICON, DELAY_FILL_COLOR } from '../constants/Style';
import ConfirmationDialog from './ConfirmationDialog';

import {
  bucketMinForDisplay,
  minutesInAnHour,
  minutesInAMonth
} from '../util/TimeUtils';
import './stylesheets/DelayNodeModal.scss';

type DelayNodeState = {
  durationMinutes: number,
  unit: string,
  hasValidDuration: boolean,
  showConfirmationDialog: boolean
};

export type DelayNodeModalProps = {
  nodeExistsInFlow: boolean,
  duration_minutes: number,
  unit: string,
  height: number,
  rx: number,
  ry: number,
  submitFormCallback: Function,
  cancelCallback?: Function,
  closeCallback: Function,
  parentName: string,
  parentNodeName: string,
  editable: boolean,
  deletingNode: boolean,
  dispatch: Function
};

const MINIMUM_DURATION_IN_MIN = minutesInAnHour;
const MAXIMUM_DURATION_IN_MIN = minutesInAMonth; // 31 day max

class DelayNodeModal extends React.Component<
  DelayNodeModalProps,
  DelayNodeState
> {
  constructor(props: DelayNodeModalProps) {
    super(props);
    const { duration_minutes: durationMinutes, unit } = this.props;
    this.isDirty = false;
    this.state = {
      durationMinutes,
      unit,
      hasValidDuration: true,
      showConfirmationDialog: false
    };
  }

  componentWillUpdate() {
    this.isDirty = true;
  }

  isDirty: boolean;

  submitForm: Function = () => {
    const { durationMinutes, unit, hasValidDuration } = this.state;
    if (hasValidDuration) {
      this.props.submitFormCallback({
        duration_minutes: durationMinutes,
        unit,
        node_type: DELAY_TYPE
      });
    }
  };

  updateDurationMinutes: Function = ({ minutes, unit }) => {
    if (
      minutes >= MINIMUM_DURATION_IN_MIN &&
      minutes <= MAXIMUM_DURATION_IN_MIN
    ) {
      this.setState({
        durationMinutes: minutes,
        unit: unit || this.state.unit,
        hasValidDuration: true
      });
    } else {
      this.setState({ hasValidDuration: false });
    }
  };

  close: Function = () => {
    if (this.props.editable && this.isDirty) {
      this.setState({
        showConfirmationDialog: true
      });
    } else {
      this.closeForReal();
    }
  };

  closeForReal: Function = () => {
    this.props.closeCallback();
  };

  cancel: Function = () => {
    (this.props.cancelCallback
      ? this.props.cancelCallback
      : this.props.closeCallback)();
  };

  toggleShowDeleteModal: Function = () => {
    const { deletingNode, dispatch } = this.props;
    if (deletingNode) {
      dispatch(stopDeletingNode());
    } else {
      dispatch(startDeletingNode());
    }
  };

  hideConfirmationDialog: Function = () => {
    this.setState({ showConfirmationDialog: false });
  };

  focusDurationField: Function = div => {
    if (div) {
      const input = div.getElementsByClassName('DurationPicker__Input')[0];
      input.focus();
    }
  };

  render() {
    const {
      nodeExistsInFlow,
      duration_minutes: durationMinutes,
      unit,
      height,
      rx,
      ry,
      parentName,
      parentNodeName,
      editable,
      deletingNode
    } = this.props;
    const { hasValidDuration } = this.state;

    if (deletingNode) {
      return (
        <DeleteNodeModal
          cancelModal={this.toggleShowDeleteModal}
          closeModal={this.close}
        />
      );
    }

    const previousNodeName = parentNodeName || parentName;

    return (
      <div>
        <div className="DelayNodeModal__Title">
          <svg
            style={{
              height: '60px',
              width: '100%'
            }}
          >
            {/* todo: this may need a larger icon than the collapsed node */}
            <StandardNode
              height={height}
              rx={rx}
              ry={ry}
              icon={DELAY_ICON}
              iconFill={DELAY_FILL_COLOR}
              botLeftRounded={false}
            >
              <text
                fill="#555555"
                y="12"
                style={{
                  fontWeight: '300',
                  fontSize: '24px'
                }}
              >
                {nodeExistsInFlow
                  ? `${editable ? 'Edit ' : ''}Wait Timer`
                  : `Add Wait Timer`}
              </text>
            </StandardNode>
          </svg>
          <button
            className="DelayNodeModal__CloseButton"
            onClick={this.close}
            tabIndex="-1"
          >
            &times;
          </button>
        </div>
        <div className="DelayNodeModal__Content">
          <div className="DelayNodeModal__MainText">
            {editable
              ? `Select how long you want to wait before users proceed to the next
            step in the workflow.`
              : ``}
          </div>
          <div className="DelayNodeModal__Form">
            <span style={{ fontWeight: '600', display: 'inline-block' }}>
              Wait for{' '}
            </span>
            <div
              className="DelayNodeModal__InputsContainer"
              ref={this.focusDurationField}
            >
              <DurationPicker
                updateTime={this.updateDurationMinutes}
                minutes={durationMinutes}
                unit={unit}
                disabled={!editable}
              />
              <span
                style={{
                  fontWeight: '600',
                  display: 'inline-block',
                  padding: '0.5rem'
                }}
              >
                after
              </span>
              <span
                style={{
                  color: DELAY_FILL_COLOR,
                  fontWeight: '600',
                  display: 'inline-block',
                  textTransform: 'capitalize'
                }}
                title={previousNodeName}
              >
                {`${previousNodeName}.`}
              </span>
            </div>
          </div>
          {nodeExistsInFlow && (
            <button
              className={classnames('DelayNodeModal__DeleteNodeButton', {
                hidden: !editable
              })}
              tabIndex={-1}
              onClick={this.toggleShowDeleteModal}
            >
              Delete this wait timer
            </button>
          )}
        </div>
        <div
          className={classnames('DelayNodeModal__ControlButtons', {
            hidden: !editable
          })}
        >
          <div>
            <Button tabIndex={-1} onClick={this.cancel}>
              Cancel
            </Button>
            {!hasValidDuration ? (
              <Tooltip
                content={`Please select a duration between ${bucketMinForDisplay(
                  MINIMUM_DURATION_IN_MIN
                )} and ${bucketMinForDisplay(MAXIMUM_DURATION_IN_MIN)}`}
              >
                <Button
                  tabIndex={-1}
                  type="primary"
                  onClick={this.submitForm}
                  disabled
                >
                  {nodeExistsInFlow ? 'Update' : 'Add'}
                </Button>
              </Tooltip>
            ) : (
              <Button tabIndex={-1} type="primary" onClick={this.submitForm}>
                {nodeExistsInFlow ? 'Update' : 'Add'}
              </Button>
            )}
          </div>
        </div>
        <ConfirmationDialog
          showConfirmationDialog={this.state.showConfirmationDialog}
          onCancel={this.hideConfirmationDialog}
          onDontSave={this.closeForReal}
          onSave={this.submitForm}
          nodeTitle="the Delay Listener"
        />
      </div>
    );
  }
}

const mapStateToProp = (state, ownProps) => {
  const { currentVersionId, currentOrchestrationId } = ownProps;
  const {
    tree: { selectedNode, nodesList, deletingNode },
    api: { versionsByOrchestrationId }
  } = state;
  const node = nodesList.find(n => n.data.name === selectedNode);
  const nodeExistsInFlow = node && node.data.node_type !== ADD_NODE_TYPE;
  const { [currentVersionId]: version = {} } = versionsByOrchestrationId[
    currentOrchestrationId
  ];
  const { nodes: { [ownProps.parentName]: parent = {} } = {} } = version;
  return {
    nodeExistsInFlow,
    parentNodeName: parent.display_name || parent.node_type,
    deletingNode
  };
};

export default connect(mapStateToProp)(DelayNodeModal);



// WEBPACK FOOTER //
// ./src/components/DelayNodeModal.jsx